import Button from 'components/Button';
import Field from 'components/Form/Field';
import FormActions from 'components/Form/FormActions';
import FormResult from 'components/Form/FormResult';
import InputText from 'components/Form/InputText';
import KeyValue from 'components/Layout/KeyValue';
import SideBySide from 'components/Layout/SideBySide';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import Value from 'components/Value';
import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {t} from 'ttag';
import {onApiErrorAxios} from 'utils/request-axios/utils';

import {useUpdateNumberSettings} from './services';
import {BackendData, NumberSetting} from './types';

const _defaultValues = (
    values: BackendData['data']['data'],
): Record<string, string> => {
    const result: Record<string, string> = {};
    values.forEach((value) => {
        result[value.name] = value.value;
    });
    return result;
};

const Row: FC<{disabled: boolean} & NumberSetting> = ({
    disabled,
    label,
    name,
}) => <InputText disabled={disabled} label={label} name={name} />;

const Page = () => {
    const [isEditing, setIsEditing] = useState(false);

    const {data} = useBackend<BackendData>();
    const [defaultValues, setDefaultValues] = useState<Record<string, string>>(
        _defaultValues(data.data),
    );
    const methods = useForm({
        defaultValues: defaultValues,
    });
    const service = useUpdateNumberSettings();

    const handleSubmit = async (formData: Record<string, string>) => {
        await service
            .mutateAsync(formData)
            .then(() => {
                setIsEditing(false);
                setDefaultValues(formData);
                methods.reset(formData);
                methods.setError('root.successMessage', {
                    message: t`Settings saved.`,
                    type: 'success',
                });
            })
            .catch(onApiErrorAxios(methods));
    };

    return (
        <div>
            <FormProvider {...methods}>
                <FormResult className="mb-4" />
                <NavigationTabs.DocumentSettings selection="numbering" />
                <SectionHeader
                    button={{
                        disabled: isEditing,
                        icon: 'edit',
                        kind: 'secondary',
                        label: t`Edit`,
                        onClick: () => setIsEditing(true),
                    }}
                    subtitle={t`Choose the base number for each document type. When creating a new document, we will take the next available number starting from your selection.`}
                    title={t`Numbering Settings`}
                />
                <form onSubmit={methods.handleSubmit(handleSubmit)}>
                    <SideBySide className="mt-4 max-w-xl">
                        {isEditing &&
                            data.data.map((row) => (
                                <Row
                                    key={row.name}
                                    {...row}
                                    disabled={!isEditing}
                                />
                            ))}
                        {!isEditing && (
                            <KeyValue keyWidth={10}>
                                {data.data.map((row) => (
                                    <Field
                                        key={row.name}
                                        label={row.label}
                                        type="value"
                                    >
                                        <Value
                                            value={{
                                                type: 'string',
                                                value: methods.getValues(
                                                    row.name,
                                                ),
                                            }}
                                        />
                                    </Field>
                                ))}
                            </KeyValue>
                        )}
                        {isEditing && (
                            <FormActions>
                                <Button
                                    aria-label={t`Cancel`}
                                    disabled={!isEditing || service.isLoading}
                                    kind="secondary"
                                    onClick={() => {
                                        methods.reset(defaultValues);
                                        setIsEditing(false);
                                    }}
                                    type="button"
                                >{t`Cancel`}</Button>
                                <Button
                                    aria-label={t`Save`}
                                    disabled={!isEditing || service.isLoading}
                                    kind="primary"
                                    type="submit"
                                >{t`Save`}</Button>
                            </FormActions>
                        )}
                    </SideBySide>
                </form>
            </FormProvider>
        </div>
    );
};

export default Page;
